// Pure helpers for event circles: times in the event's zone, status and
// countdowns, schedule text, and the create/edit form's validation and bodies.
import type { Schemas } from "@10twenty/api";

type CircleEvent = Schemas["CircleEvent"];
type EventStatus = CircleEvent["status"];

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** The longest event the server accepts. */
export const MAX_EVENT_MS = 14 * DAY_MS;
export const DESCRIPTION_MAX = 500;
export const LABEL_MAX = 80;
/** A new event's default length. */
const DEFAULT_LENGTH_MS = 3 * HOUR_MS;

/** Code points, as the server counts lengths. */
const codePoints = (text: string) => [...text].length;

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

function zoneParts(ms: number, zone: string): number[] {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return [get("year"), get("month"), get("day"), get("hour"), get("minute"), get("second")];
}

/** How far `zone` is ahead of UTC at instant `ms`. */
function zoneOffsetMs(ms: number, zone: string): number {
  const [y, mo, d, h, mi, s] = zoneParts(ms, zone);
  return Date.UTC(y!, mo! - 1, d, h, mi, s) - Math.floor(ms / 1000) * 1000;
}

/** Whether the runtime knows `zone` (an IANA name). */
export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone.length > 0;
  } catch {
    return false;
  }
}

/**
 * The instant (epoch ms) a `datetime-local` value (`YYYY-MM-DDTHH:mm`) names
 * in `zone`, or null if it is malformed. A wall time skipped by a clock
 * change lands just after it; a repeated one picks the earlier instant.
 */
export function zonedToMs(local: string, zone: string): number | null {
  const match = LOCAL.exec(local);
  if (!match || !isTimeZone(zone)) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const wall = Date.UTC(y!, mo! - 1, d, h, mi);
  if (Number.isNaN(wall)) return null;
  // Try the offsets in force just before and after: the earlier fitting instant wins.
  const before = zoneOffsetMs(wall - DAY_MS, zone);
  const candidates = [before, zoneOffsetMs(wall + DAY_MS, zone)]
    .map((offset) => wall - offset)
    .filter((ms) => ms + zoneOffsetMs(ms, zone) === wall)
    .sort((a, b) => a - b);
  // In a skipped hour the earlier offset moves the time forward past the gap.
  return candidates[0] ?? wall - before;
}

/** The `datetime-local` value for instant `ms` in `zone`. */
export function msToZoned(ms: number, zone: string): string {
  const [y, mo, d, h, mi] = zoneParts(ms, zone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${y}-${pad(mo!)}-${pad(d!)}T${pad(h!)}:${pad(mi!)}`;
}

/** Where the event stands at `nowMs` (the server's `status` is as of the read). */
export function eventStatus(event: Pick<CircleEvent, "starts_at" | "ends_at">, nowMs: number): EventStatus {
  if (nowMs < Date.parse(event.starts_at)) return "upcoming";
  return nowMs < Date.parse(event.ends_at) ? "live" : "ended";
}

export const STATUS_LABELS: Record<EventStatus, string> = { upcoming: "Upcoming", live: "Live", ended: "Ended" };

/** "2 days" / "3 h 20 min" / "5 min" (at least a minute). */
export function durationText(ms: number): string {
  if (ms >= 2 * DAY_MS) return `${Math.floor(ms / DAY_MS)} days`;
  if (ms >= HOUR_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  return `${Math.max(1, Math.floor(ms / MINUTE_MS))} min`;
}

/** "Starts in 3 h 20 min" / "Live · ends in 45 min" / "Ended". */
export function countdownText(event: Pick<CircleEvent, "starts_at" | "ends_at">, nowMs: number): string {
  switch (eventStatus(event, nowMs)) {
    case "upcoming":
      return `Starts in ${durationText(Date.parse(event.starts_at) - nowMs)}`;
    case "live":
      return `Live · ends in ${durationText(Date.parse(event.ends_at) - nowMs)}`;
    case "ended":
      return "Ended";
  }
}

function dayText(ms: number, zone: string, locale: string | undefined): string {
  return new Intl.DateTimeFormat(locale, { timeZone: zone, weekday: "short", day: "numeric", month: "short" }).format(
    ms,
  );
}

function timeText(ms: number, zone: string, locale: string | undefined): string {
  return new Intl.DateTimeFormat(locale, { timeZone: zone, hour: "2-digit", minute: "2-digit" }).format(ms);
}

/**
 * "Sat 10 Oct, 14:00–18:00" or "Sat 10 Oct, 14:00 – Sun 11 Oct, 12:00", in
 * the event's zone; the zone is named when it isn't the viewer's.
 */
export function scheduleText(
  event: Pick<CircleEvent, "starts_at" | "ends_at" | "time_zone">,
  viewerZone: string,
  locale?: string,
): string {
  const zone = isTimeZone(event.time_zone) ? event.time_zone : "UTC";
  const start = Date.parse(event.starts_at);
  const end = Date.parse(event.ends_at);
  const startDay = dayText(start, zone, locale);
  const endDay = dayText(end, zone, locale);
  const range =
    startDay === endDay
      ? `${startDay}, ${timeText(start, zone, locale)}–${timeText(end, zone, locale)}`
      : `${startDay}, ${timeText(start, zone, locale)} – ${endDay}, ${timeText(end, zone, locale)}`;
  return zone === viewerZone ? range : `${range} (${zone.replaceAll("_", " ")})`;
}

/** "Deleted on Mon 9 Nov" for an ended event, in the viewer's zone. */
export function deletionText(event: Pick<CircleEvent, "deletes_at">, viewerZone: string, locale?: string): string {
  return `Deleted on ${dayText(Date.parse(event.deletes_at), viewerZone, locale)}`;
}

/** The create/edit form, with times as `datetime-local` values in `timeZone`. */
export interface EventDraft {
  name: string;
  start: string;
  end: string;
  timeZone: string;
  description: string;
  meetingPoint: { lat: number; lon: number } | null;
  meetingLabel: string;
}

/** A blank form: starting at the next full hour in `zone`, three hours long. */
export function newEventDraft(nowMs: number, zone: string): EventDraft {
  // Wall-clock arithmetic in the zone, as if it were UTC: a full hour there, whatever its offset.
  const wall = Date.parse(`${msToZoned(nowMs, zone)}:00Z`);
  const start = Math.floor(wall / HOUR_MS) * HOUR_MS + HOUR_MS;
  const local = (ms: number) => new Date(ms).toISOString().slice(0, 16);
  return {
    name: "",
    start: local(start),
    end: local(start + DEFAULT_LENGTH_MS),
    timeZone: zone,
    description: "",
    meetingPoint: null,
    meetingLabel: "",
  };
}

/** The form for editing an event as it is. */
export function draftFromEvent(name: string, event: CircleEvent): EventDraft {
  const zone = isTimeZone(event.time_zone) ? event.time_zone : "UTC";
  return {
    name,
    start: msToZoned(Date.parse(event.starts_at), zone),
    end: msToZoned(Date.parse(event.ends_at), zone),
    timeZone: zone,
    description: event.description ?? "",
    meetingPoint: event.meeting_point ? { lat: event.meeting_point.lat, lon: event.meeting_point.lon } : null,
    meetingLabel: event.meeting_point?.label ?? "",
  };
}

/**
 * What stops the form being saved, as the server would refuse it, or null.
 * `timesFixed` (an ended event, whose start and end can no longer move)
 * skips the time checks; for an edit, `original` keeps untouched times as
 * they are (see {@link draftTimes}).
 */
export function draftProblem(
  draft: EventDraft,
  nowMs: number,
  timesFixed = false,
  original?: CircleEvent,
): string | null {
  if (!draft.name.trim()) return "Give the event a name.";
  if (!isTimeZone(draft.timeZone)) return "Choose a time zone.";
  if (!timesFixed) {
    const { start, end } = draftTimes(draft, original);
    if (start === null || end === null) return "Choose when the event starts and ends.";
    if (end <= start) return "The event must end after it starts.";
    if (end - start > MAX_EVENT_MS) return "An event can last at most 14 days.";
    if (end <= nowMs) return "The event must end in the future.";
  }
  if (codePoints(draft.description.trim()) > DESCRIPTION_MAX)
    return `The description can be at most ${DESCRIPTION_MAX} characters.`;
  if (codePoints(draft.meetingLabel.trim()) > LABEL_MAX)
    return `The meeting point's name can be at most ${LABEL_MAX} characters.`;
  return null;
}

function meetingPoint(draft: EventDraft): Schemas["MeetingPoint"] | null {
  if (!draft.meetingPoint) return null;
  return { lat: draft.meetingPoint.lat, lon: draft.meetingPoint.lon, label: draft.meetingLabel.trim() || null };
}

/**
 * The draft's start and end (null if unreadable). For an edit, a time the
 * form left as it was (in an unchanged zone) is the event's own instant:
 * converting the wall time back could pick the other side of a repeated
 * hour, or drop seconds.
 */
export function draftTimes(draft: EventDraft, original?: CircleEvent): { start: number | null; end: number | null } {
  if (!original) return { start: zonedToMs(draft.start, draft.timeZone), end: zonedToMs(draft.end, draft.timeZone) };
  const shown = draftFromEvent("", original);
  const kept = (field: "start" | "end") => shown.timeZone === draft.timeZone && draft[field] === shown[field];
  return {
    start: kept("start") ? Date.parse(original.starts_at) : zonedToMs(draft.start, draft.timeZone),
    end: kept("end") ? Date.parse(original.ends_at) : zonedToMs(draft.end, draft.timeZone),
  };
}

/** `POST /circles` for a valid draft (check {@link draftProblem} first). */
export function eventCreateBody(draft: EventDraft): Schemas["CircleCreate"] {
  return {
    name: draft.name.trim(),
    kind: "event",
    event: {
      starts_at: new Date(zonedToMs(draft.start, draft.timeZone)!).toISOString(),
      ends_at: new Date(zonedToMs(draft.end, draft.timeZone)!).toISOString(),
      time_zone: draft.timeZone,
      description: draft.description.trim() || null,
      meeting_point: meetingPoint(draft),
    },
  };
}

/**
 * `PATCH /circles/{id}` with what the draft changed (null when nothing did).
 * Times are left out once the event has ended (`timesFixed`).
 */
export function eventUpdateBody(
  draft: EventDraft,
  name: string,
  event: CircleEvent,
  timesFixed: boolean,
): Schemas["CircleUpdate"] | null {
  const body: Schemas["CircleUpdate"] = {};
  const patch: Schemas["CircleEventUpdate"] = {};
  if (draft.name.trim() !== name) body.name = draft.name.trim();
  if (!timesFixed) {
    const { start, end } = draftTimes(draft, event);
    if (start !== null && start !== Date.parse(event.starts_at)) patch.starts_at = new Date(start).toISOString();
    if (end !== null && end !== Date.parse(event.ends_at)) patch.ends_at = new Date(end).toISOString();
  }
  if (draft.timeZone !== event.time_zone) patch.time_zone = draft.timeZone;
  const description = draft.description.trim() || null;
  if (description !== event.description) patch.description = description;
  const point = meetingPoint(draft);
  const old = event.meeting_point;
  const samePoint =
    point === null
      ? old === null
      : old !== null && point.lat === old.lat && point.lon === old.lon && point.label === (old.label ?? null);
  if (!samePoint) patch.meeting_point = point;
  if (Object.keys(patch).length > 0) body.event = patch;
  return Object.keys(body).length > 0 ? body : null;
}

/** The viewer's IANA zone. */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** Zones to offer: the runtime's list, with `current` included. */
export function timeZoneChoices(current: string): string[] {
  const all = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = new Set(all);
  zones.add("UTC");
  zones.add(current);
  return [...zones].sort();
}
